'use client';

import { cva, cx } from '@repo/panda/css';
import type React from 'react';
import { type CSSProperties, type HTMLAttributes, type Ref, useEffect, useRef, useState } from 'react';
const scrollableContentStyles = cva({
  base: {
    position: 'relative',
    width: 'full'
  }
});
const scrollAreaStyles = cva({
  base: {
    overflowY: 'auto',
    width: 'full',
    position: 'relative'
  }
});
const scrollAreaStylesNoOverflow = cva({
  base: {
    overflowY: 'hidden',
    width: 'full',
    position: 'relative'
  }
});
const fadeStyles = cva({
  base: {
    position: 'absolute',
    left: '0',
    width: 'full',
    height: '12',
    pointerEvents: 'none',
    zIndex: 1
  },
  variants: {
    position: {
      top: {
        top: '0',
        backgroundImage: 'linear-gradient(to bottom, rgba(0,0,0,0.35), transparent)'
      },
      bottom: {
        bottom: '0',
        backgroundImage: 'linear-gradient(to top, rgba(0,0,0,0.35), transparent)'
      }
    },
    visible: {
      true: {
        opacity: 1
      },
      false: {
        opacity: 0
      }
    }
  },
  defaultVariants: {
    visible: false
  }
});
export interface ScrollableContentClientProps extends HTMLAttributes<HTMLDivElement> {
  maxHeight?: string | number;
  children: React.ReactNode;
  scrollAreaProps?: HTMLAttributes<HTMLDivElement>;
  scrollAreaRef?: Ref<HTMLDivElement>;
  scrollAreaStyle?: React.CSSProperties;
  hideScrollbarWhenNoOverflow?: boolean;
  /** Optional border radius applied to the scroll fade indicators (e.g. '12px'). */
  fadeRadius?: string;
}
export const ScrollableContentClient: React.FC<ScrollableContentClientProps> = ({
  maxHeight,
  children,
  className,
  style,
  hideScrollbarWhenNoOverflow = false,
  fadeRadius,
  scrollAreaProps,
  scrollAreaRef,
  scrollAreaStyle,
  ...props
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const updateFades = () => {
      const hasVerticalOverflow = el.scrollHeight > el.clientHeight + 1;
      setHasOverflow(hasVerticalOverflow);
      setShowTopFade(hasVerticalOverflow && el.scrollTop > 0);
      setShowBottomFade(hasVerticalOverflow && el.scrollTop + el.clientHeight < el.scrollHeight - 1);
    };
    updateFades();
    el.addEventListener('scroll', updateFades);
    window.addEventListener('resize', updateFades);
    const resizeObserver = new ResizeObserver(updateFades);
    resizeObserver.observe(el);
    return () => {
      el.removeEventListener('scroll', updateFades);
      window.removeEventListener('resize', updateFades);
      resizeObserver.disconnect();
    };
  }, []);
  const resolvedMaxHeight = typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;
  const fadeTransition: CSSProperties = {
    transition: 'opacity 0.2s'
  };
  const topFadeStyle: CSSProperties = {
    ...fadeTransition,
    ...(fadeRadius && {
      borderTopLeftRadius: fadeRadius,
      borderTopRightRadius: fadeRadius
    })
  };
  const bottomFadeStyle: CSSProperties = {
    ...fadeTransition,
    ...(fadeRadius && {
      borderBottomLeftRadius: fadeRadius,
      borderBottomRightRadius: fadeRadius
    })
  };
  const shouldHideScrollbar = hideScrollbarWhenNoOverflow && !hasOverflow;
  const scrollAreaClassName = cx(shouldHideScrollbar ? scrollAreaStylesNoOverflow() : scrollAreaStyles(), scrollAreaProps?.className);
  const setScrollRef = (element: HTMLDivElement | null) => {
    scrollRef.current = element;
    if (typeof scrollAreaRef === 'function') {
      scrollAreaRef(element);
    } else if (scrollAreaRef) {
      scrollAreaRef.current = element;
    }
  };
  return <div className={cx(scrollableContentStyles(), className)} style={style} {...props}>
            <div {...scrollAreaProps} ref={setScrollRef} className={scrollAreaClassName} style={{
      maxHeight: resolvedMaxHeight,
      ...(scrollAreaStyle || {}),
      ...scrollAreaProps?.style
    }}>
                {children}
            </div>
            <div className={fadeStyles({
      position: 'top',
      visible: showTopFade
    })} style={topFadeStyle} aria-hidden="true" />
            <div className={fadeStyles({
      position: 'bottom',
      visible: showBottomFade
    })} style={bottomFadeStyle} aria-hidden="true" />
        </div>;
};